/**
 * @file review-orders-list.tsx
 * @module app/modules/orders-delivery/review-orders-list
 * @description Orders delivery module component for UTrade Admin.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Popover,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import APICallService from '../../../../../api/apiCallService';
import { ordersDelivery, routePlanning } from '../../../../../api/apiEndPoints';
import { useOrderQueuePolling } from '../../hooks/use-order-queue-polling';
import { fetchRoutePlanInfo } from '../../services/route-plan-info.service';
import { fetchVehicleInfo } from '../../services/vehicle-info.service';
import { updateVehicleRouteStatus, updateOrder } from '../../services/status-update.service';
import Loader from '../../../../../Global/loader';
import editIcon from '../../../../../_admin/assets/media/svg_uTrade/edit-round-blue.svg';
import CrossIcon from '../../../../../_admin/assets/media/svg_uTrade/cross-rounded-blue.svg';
import DeleteIcon from '../../../../../_admin/assets/media/svg_uTrade/trash.svg';
import greenCheck from '../../../../../_admin/assets/media/svg_uTrade/green_check.svg';
import {
  Add,
  CartonWithDozens,
  CartonWithPieces,
  Delete,
  Dozen,
  Edit,
  OrderCancelled,
  OrderQueueFailed,
  OrderQueueProcessed,
  OrdersDelivery,
  Piece,
  RouteOrderCancelled,
  RouteOrderDelivered,
  RouteOrderLoaded,
  RouteOrderOutForDelivery,
  RouteOrderTigoPesa,
  TigoPesaPayment,
} from '../../../../../utils/constants';
import { routePlanningJSON } from '../../../../../api/apiJSON/routePlanning';
import Method from '../../../../../utils/methods';
import clsx from 'clsx';
import AddOrderPlan from '../../../../modals/add-order-to-plan';
import { error, success } from '../../../../../Global/toast';
import { CustomSelectTable } from '../../../../custom/Select/CustomSelectTable';
import { CustomSelect } from '../../../../custom/Select/CustomSelect';
import { getKey, setKey } from '../../../../../Global/history';
import { listOrdersDelivery } from '../../../../../utils/storeString';
import { orderDeliveryJSON } from '../../../../../api/apiJSON/order';
import ConfirmChanges from '../../../../modals/confirm-changes';
import { useAuth } from '../../../auth';
import { warehouse as warehouseStoreString } from '../../../../../utils/storeString';
import CustomDatePicker from '../../../../custom/DateRange/DatePicker';
import SelectPlanDateReview from '../../../../modals/select-plan-date-review';
import { useWarehouse } from '../../../../context/WarehouseContext';
import SummaryInfoCard from '../../../_common/components/summary-info-card';
import ImageWithFallback from '../../../_common/components/image-with-fallback';

const ReviewOrderList = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [currentIndex, setCurrentIndex] = useState<any>(-1);
  const [outForDelivery, setoutForDelivery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderRemoveLoader, setOrderRemoveLoader] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(true);
  const [details, setDetails] = useState<any>();
  const [confirmModal, setConfirmModal] = useState(false);
  const [show, setShow] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [singleVehicle, setSingleVehicle] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState<any>();
  const [planInfo, setPlanInfo] = useState<any>();
  const [selectedProduct, setSelectedProduct] = useState<any>([]);
  const [selectedProductValidation, setSelectedProductValidation] =
    useState<any>([]);
  const [showSelectDateModal, setSelectDateModal] = useState<boolean>(false);

  const [quantityError, setQuantityError] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<any>(null);
  const [originalDetails, setOriginalDetails] = useState<any>(null);
  const [initDetails, setInitDetails] = useState<any>(null);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [confirm, setConfirm] = useState<any>(null);
  const [orderId, setOrderId] = useState<any>();
  const [isOrderPlaced, setIsOrderPlaced] = useState<any>(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [showDatePicker, setShowDatePicker] = useState<any>(false);
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([
    new Date(),
  ]);
  const [validation, setValidation] = useState<{
    [key: string]: {
      isInvalid: boolean;
      remainingQty: number;
    };
  }>({});
  const [removeOrderEnable, setRemoveOrderEnable] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<any>([]);
  const [districtIds, setDistrictIds] = useState([]);

  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchPlanInfo();
      setFetchLoader(false);
    })();
  }, []);
  const fetchPlanInfo = async () => {
    let response: any = await fetchRoutePlanInfo(
      state.routeId,
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    setPlanInfo(response);
    setStartDate(new Date(response.record.date));
    setSingleVehicle(response.vehicles.length == 1);
    setVehicleInfo(response.vehicles);
    const vehiclesdistrictIds: any =
      response?.vehicles[0]?.vehicle?.district?.map(
        (item: any) => item?.reference
      );
    setDistrictIds(vehiclesdistrictIds);
    let vehicle: any = getKey(listOrdersDelivery.selectedVehicle);
    if (vehicle) {
      const filteredRecords = response.vehicles.filter(
        (record: any) => record.vehicle.reference === vehicle
      );
      if (filteredRecords.length) {
        setSelectedDriver(filteredRecords[0]);
      } else {
        setSelectedDriver(response.vehicles[0].vehicle.reference);
        setKey(
          listOrdersDelivery.selectedVehicle,
          response.vehicles[0].vehicle.reference,
          false
        );
      }
    } else {
      vehicle = response.vehicles[0].vehicle.reference;
      setKey(
        listOrdersDelivery.selectedVehicle,
        response.vehicles[0].vehicle.reference,
        false
      );
      setSelectedDriver(response.vehicles[0]);
    }
    await fetchDeliveryPlans(state.routeId, vehicle);
  };
  const fetchDeliveryPlans = async (routeId: string, vehicleId: string) => {
    let response: any = await fetchVehicleInfo(
      routeId,
      vehicleId,
      OrdersDelivery,
      { forReview: true },
      selectedWarehouse?.value || null
    );
    let temp = JSON.parse(JSON.stringify({ ...response }));
    temp.orders = temp.orders.reverse();
    setDetails(JSON.parse(JSON.stringify({ ...temp })));
    setOriginalDetails(JSON.parse(JSON.stringify({ ...temp })));
    setInitDetails(JSON.parse(JSON.stringify({ ...temp })));
  };
  const handleAddOrderToList = async (data: any) => {
    setLoading(true);
    let params = {
      order: data,
    };
    let apiService = new APICallService(
      routePlanning.addOrder,
      routePlanningJSON.addOrder(params),
      {
        routeId: planInfo.record._id,
        vehicleId: selectedDriver.vehicle.reference,
      },
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {
      navigate('/orders/review-order-list', {
        state: {
          routeId: planInfo.record._id,
          vehicleId: selectedDriver.vehicle.reference,
        },
      });
    }
    setLoading(false);
  };
  const handleCancel = async () => {
    setCancelLoading(true);
    let params = {
      status: RouteOrderCancelled,
    };
    let apiService = new APICallService(
      routePlanning.vehicleRouteStatus,
      routePlanningJSON.vehicleRouteStatusUpdate(params),
      {
        routeId: planInfo.record._id,
        vehicleId: selectedDriver.vehicle.reference,
      },
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {
      navigate('/orders/route-details', {
        state: {
          routeId: planInfo.record._id,
          vehicleId: selectedDriver.vehicle.reference,
        },
      });
    }
    setCancelLoading(false);
    setConfirmModal(false);
  };
  const handleRemoveOrderFromList = async (data: any) => {
    setOrderRemoveLoader(true);
    // if (details.orders.length === 1) {
    //   error("At least one order is required");
    // } else {
    // setCurrentIndex(index);
    let params = {
      orders: data,
    };
    let apiService = new APICallService(
      routePlanning.removeOrder,
      params,
      {
        routeId: planInfo.record._id,
        vehicleId: selectedDriver.vehicle.reference,
      },
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {
      setEditingVariantIndex(null);
      setEditingIndex(null);
      await fetchDeliveryPlans(
        planInfo.record._id,
        selectedDriver.vehicle.reference
      );
    }
    setCurrentIndex(-1);
    // }
    setRemoveOrderEnable(false);
    setSelectedOrders([]);
    setOrderRemoveLoader(false);
  };

  const handleOrderSelect = (ids: any) => {
    let temp = [...selectedOrders];
    if (temp.includes(ids)) {
      temp = temp.filter((item) => item != ids);
    } else {
      temp.push(ids);
    }
    setSelectedOrders(temp);
  };

  const completeReview = async () => {
    setLoading(true);
    let apiService = new APICallService(
      routePlanning.completeReview,
      {},
      {
        id: planInfo.record._id,
      },
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {

      navigate('/orders/goods-loading-details', {
        state: {
          vehicleId: selectedDriver.vehicle.reference,
          routeId: planInfo.record._id,
        },
      });
    }
    setLoading(false);
  };

  const changePlanDate = async (date: any) => {
    setShowDatePicker(!showDatePicker);
    setDateLoading(true);
    let apiService = new APICallService(
      routePlanning.updatePlanDate,
      {
        date: Method.convertDateToFormat(date, 'YYYY-MM-DD'),
      },
      {
        id: planInfo.record._id,
      },
      '',
      '',
      '',
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response) {
      setFetchLoader(true);
      await fetchPlanInfo();
      setFetchLoader(false);
    }
    setSelectDateModal(false);
    setDateLoading(false);
  };
  const openMenuOnClick = async () => {
    setShow(true);
  };
  const onMenuClose = async () => {
    setShow(false);
  };
  const onMenuOpen = async () => {
    setShow(true);
  };
  const handleOption = async (event: any) => {
    if (event.value === 1) {
      setConfirmModal(true);
    }
  };
  const handleChange = async (event: any) => {
    setFetchLoader(true);
    setSelectedDriver(event);
    setKey(listOrdersDelivery.selectedVehicle, event.vehicle.reference, false);
    await fetchDeliveryPlans(state.routeId, event.vehicle.reference);
    setFetchLoader(false);
  };
  function calculateTotalCostWithoutTax(stockCount: any, amount: any) {
    const price = stockCount * amount;
    return price;
  }
  const handleEditClick = (index: any, vIndex: any) => {
    setDetails(originalDetails);
    setEditingVariantIndex(vIndex);
    setEditingIndex(index);
  };
  const handleSaveClick = async (index: any) => {
    await fetchDeliveryPlans(state.routeId, selectedDriver.vehicle.reference);
    setEditingVariantIndex(null);
    setEditingIndex(null);
  };
  const handleRemoveProduct = async (
    isRemove: boolean,
    quantIndex: any,
    index: any,
    product: any,
    order?: any
  ) => {
    setCancelLoading(true);

    if (isRemove) {
      const temp = { ...product };
      temp.stockCount = 0;
      setQuantityError(false);
      setSelectedProduct(temp);

      const newDetails = { ...details };

      if (newDetails.orders && newDetails.orders[index]) {
        const updatedOrder = { ...newDetails.orders[index] };

        // Check if the variant already exists in the order, based on clonedRef or variant
        const existingIndex = updatedOrder.variants.findIndex(
          (v: any) =>
            v.clonedRef === temp.clonedRef || v.variant === temp.variant
        );

        // If it exists, update it; otherwise, update the quantIndex
        const targetIndex = existingIndex !== -1 ? existingIndex : quantIndex;

        // Update the variant at the found or selected index
        updatedOrder.variants[targetIndex] = { ...temp };

        // Also update the routeVehicle at the corresponding index
        if (
          updatedOrder.routeVehicle &&
          updatedOrder.routeVehicle[targetIndex]
        ) {
          updatedOrder.routeVehicle[targetIndex] = {
            ...updatedOrder.routeVehicle[targetIndex],
            ...temp,
          };
        }

        // Apply the updated order back to the list
        newDetails.orders[index] = updatedOrder;
      }

      // Build variants array without pushing (same pattern)
      let variants: any = [];
      newDetails.orders[index].variants.map((val: any) => {
        variants.push({
          variant: val.variant,
          stockCount: val.stockCount,
          quantityType: val.quantityType,
          discounts: val.clonedQuantityType.discountAmtEnabled
            ? val.clonedQuantityType
            : [],
        });
      });

      let data: any = {
        variants: variants,
        record: newDetails.orders[index]._id,
        buyer: newDetails.orders[index].customer.reference,
        address: newDetails.orders[index].address.reference,
      };
      let response: any = await updateOrder(
        data,
        planInfo.record._id,
        selectedDriver.vehicle.reference,
        selectedWarehouse?.value || null
      );
      if (response) {
        const title = details.orders[index].routeVehicle.variants.find(
          (val: any) => val.variant._id == product.variant
        )?.variant.title;

        setFetchLoader(true);

        setOrderId(response.queueId);
        setIsOrderPlaced(true);
        setFetchLoader(false);
      }
    } else {
      setCurrentIndex(index);
      let params = {
        orders: [product],
      };
      let apiService = new APICallService(
        routePlanning.removeOrder,
        params,
        {
          routeId: planInfo.record._id,
          vehicleId: selectedDriver.vehicle.reference,
        },
        '',
        '',
        '',
        OrdersDelivery,
        selectedWarehouse?.value || null
      );
      let response: any = await apiService.callAPI();
      if (response) {
        let apiService = new APICallService(
          ordersDelivery.status,
          orderDeliveryJSON.updateOrderStatus({ status: OrderCancelled }),
          { id: product },
          '',
          '',
          '',
          OrdersDelivery,
          selectedWarehouse?.value || null
        );
        let response = await apiService.callAPI();
        if (response) {
          await fetchDeliveryPlans(
            planInfo.record._id,
            selectedDriver.vehicle.reference
          );
        }
      }
      setCurrentIndex(-1);
    }
    setConfirm(null);
    setConfirmModal(false);
    setCancelLoading(false);
  };

  const calculateRemainingQty = (
    selectedVariantType: any,
    type: any
  ): number => {
    return (
      selectedVariantType.inventoryInfo[0]?.quantityTypes.find(
        (sval: any) => sval.type === type
      )?.remainingQty ?? 0
    );
  };
  const handleQuantityChange = async (
    value: any,
    index: number,
    product: any,
    quantIndex: number | any,
    type: any,
    productInventory: any
  ) => {
    // First, handle the conversion of value
    value = parseInt(Method.trimLeadingZeros(value));
    product = JSON.parse(JSON.stringify(product));
    productInventory = JSON.parse(JSON.stringify(productInventory));

    const validationKey = `${type}-${productInventory.variant._id || ''}`; // Assuming product has an option key
    const item = productInventory.variant.inventoryInfo[0]?.quantityTypes.find(
      (qt: any) => qt.type === type
    );
    let stockCount = initDetails.orders[index].variants[quantIndex].stockCount;

    const {
      quantityTypes,
      dozensInCarton = 0,
      piecesInCarton = 0,
      piecesInDozen = 0,
    } = productInventory.variant;

    let remainingQty = 0;

    // --- Validation logic ---
    if (type === Dozen) {
      const dozenQty = calculateRemainingQty(productInventory.variant, Dozen);
      const cartonWithDozenQty = calculateRemainingQty(
        productInventory.variant,
        CartonWithDozens
      );
      remainingQty =
        stockCount + dozenQty + cartonWithDozenQty * dozensInCarton;
    } else if (type === Piece) {
      const pieceQty = calculateRemainingQty(productInventory.variant, Piece);
      let dozenQty =
        calculateRemainingQty(productInventory.variant, CartonWithDozens) *
        dozensInCarton;
      let dozenIndex =
        productInventory.variant.inventoryInfo[0]?.quantityTypes.findIndex(
          (sval: any) => sval.type === Dozen
        );
      if (dozenIndex !== -1) {
        productInventory.variant.inventoryInfo[0].quantityTypes[
          dozenIndex
        ].remainingQty =
          productInventory.variant.inventoryInfo[0].quantityTypes[dozenIndex]
            .remainingQty + dozenQty;
        dozenQty =
          calculateRemainingQty(productInventory.variant, Dozen) *
          piecesInDozen;
      }
      const cartonQty =
        calculateRemainingQty(productInventory.variant, CartonWithPieces) *
        piecesInCarton;

      remainingQty = stockCount + pieceQty + dozenQty + cartonQty;
    } else {
      if (item) {
        remainingQty = stockCount + item?.remainingQty || 0;
      } else {
        remainingQty = stockCount;
      }
    }

    let validatedValue = value;
    if (value > remainingQty || !remainingQty) {
      validatedValue = remainingQty;
    }

    // --- Update product ---
    const temp = { ...product };
    temp.stockCount = validatedValue || 1;

    // --- Handle specific case when value is 0 or invalid ---
    setQuantityError(false);
    if (value === 0 || !value) {
      temp.stockCount = 1;
      setQuantityError(true);
    }

    const newDetails = { ...details };
    if (newDetails.orders && newDetails.orders[index]) {
      const updatedOrder = { ...newDetails.orders[index] };

      // Check if the variant already exists in the order, based on clonedRef or variant
      const existingIndex = updatedOrder.variants.findIndex(
        (v: any) => v.clonedRef === temp.clonedRef || v.variant === temp.variant
      );

      // If it exists, update it; otherwise, update the quantIndex
      const targetIndex = existingIndex !== -1 ? existingIndex : quantIndex;

      // Update the variant at the found or selected index
      updatedOrder.variants[targetIndex] = { ...temp };

      // Also update the routeVehicle at the corresponding index
      if (updatedOrder.routeVehicle && updatedOrder.routeVehicle[targetIndex]) {
        updatedOrder.routeVehicle[targetIndex] = {
          ...updatedOrder.routeVehicle[targetIndex],
          ...temp,
        };
      }

      // Apply the updated order back to the list
      newDetails.orders[index] = updatedOrder;
    }

    setDetails(newDetails);

    // --- Set validation ---
    const newValidation: any = {
      [validationKey]: {
        isInvalid: value > remainingQty,
        remainingQty: remainingQty,
      },
    };
    setValidation(newValidation);
  };

  const handleSave = async (
    value: any,
    index: number,
    product: any,
    quantIndex: number
  ) => {
    const temp = { ...product };
    temp.stockCount = value;
    setQuantityError(false);
    if (value == 0 || !value) {
      setQuantityError(true);
    }
    setSelectedProduct(temp);
    const newDetails = { ...details };
    if (newDetails.orders && newDetails.orders[index]) {
      const updatedOrder = { ...newDetails.orders[index] };

      // Check if the variant already exists in the order, based on clonedRef or variant
      const existingIndex = updatedOrder.variants.findIndex(
        (v: any) => v.clonedRef === temp.clonedRef || v.variant === temp.variant
      );

      // If it exists, update it; otherwise, update the quantIndex
      const targetIndex = existingIndex !== -1 ? existingIndex : quantIndex;

      // Update the variant at the found or selected index
      updatedOrder.variants[targetIndex] = { ...temp };

      // Also update the routeVehicle at the corresponding index
      if (updatedOrder.routeVehicle && updatedOrder.routeVehicle[targetIndex]) {
        updatedOrder.routeVehicle[targetIndex] = {
          ...updatedOrder.routeVehicle[targetIndex],
          ...temp,
        };
      }

      // Apply the updated order back to the list
      newDetails.orders[index] = updatedOrder;
    }

    // Build variants array without pushing (same pattern)
    let variants: any = [];
    newDetails.orders[index].variants.map((val: any) => {
      variants.push({
        variant: val.variant,
        stockCount: val.stockCount,
        quantityType: val.quantityType,
        discounts: val.clonedQuantityType.discountAmtEnabled
          ? val.clonedQuantityType
          : [],
      });
    });
    let data: any = {
      variants: variants,
      record: newDetails.orders[index]._id,
      buyer: newDetails.orders[index].customer.reference,
      address: newDetails.orders[index].address.reference,
    };
    setOrderLoading(true);
    let response: any = await updateOrder(
      data,
      planInfo.record._id,
      selectedDriver.vehicle.reference,
      selectedWarehouse?.value || null
    );
    if (response) {
      const title = details.orders[index].routeVehicle.variants.find(
        (val: any) => val.variant._id == product.variant
      )?.variant.title;
      setOrderId(response.queueId);
      setIsOrderPlaced(true);

    } else {
      setOrderLoading(false);
    }
  };
  const handleOnKeyPress = (event: any) => {
    var charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
      return false;
    }
    return true;
  };
  function ProductPriceWithoutTax(
    { productVal, orderVal }: any,
    partialDelivery = false
  ) {
    const isPharmacyOrder = isPharmacyOrderForOrder(orderVal);
    const stockCount = productVal.stockCount;
    const priceData = isPharmacyOrder
      ? {
          unitPrice: Number(productVal?.amount || 0),
          originalPrice: Number(
            productVal?.originalAmount ?? productVal?.amount ?? 0
          ),
        }
      : getVariantUnitAndTotalPriceByQuantityType(
          productVal?.clonedQuantityType,
          stockCount
        );

    let priceContent = <></>;
    if (partialDelivery) {
      priceContent = (
        <>TSh {Method.getGeneralizedAmount(priceData.unitPrice)} </>
      );
    } else {
      priceContent = (
        <>
          TSh{' '}
          {Method.getGeneralizedAmount(
            calculateTotalCostWithoutTax(stockCount, priceData.unitPrice)
          )}{' '}
          {priceData.unitPrice !== priceData.originalPrice ? (
            <span className="text-decoration-line-through text-gray ms-1">
              TSh{' '}
              {Method.getGeneralizedAmount(
                calculateTotalCostWithoutTax(
                  stockCount,
                  priceData.originalPrice
                )
              )}
            </span>
          ) : (
            <></>
          )}
        </>
      );
    }
    return <span className="fs-16 fw-600">{priceContent}</span>;
  }
  function getVariantUnitAndTotalPriceByQuantityType(
    quantityTypeObj: any,
    quantity: any = 0
  ) {
    let unitPrice = quantityTypeObj['amount'] || 0;
    let originalPrice = quantityTypeObj['amount'] || 0;
    if (quantityTypeObj['discountAmtEnabled']) {
      unitPrice = quantityTypeObj['discountAmt'] || 0;
    } else if (quantityTypeObj['discountByQuantitiesEnabled']) {
      let availableDiscountRanges =
        quantityTypeObj['discountsByQuantities'] || [];
      if (availableDiscountRanges.length > 0) {
        availableDiscountRanges.sort((a: any, b: any) => a['min'] - b['min']);
        if (quantity < availableDiscountRanges[0]['min']) {
          //consider original price as it is
        } else {
          let { matchedRange, maxRange } = availableDiscountRanges.reduce(
            (p: any, c: any) => {
              if (quantity >= c['min'] && quantity <= c['max']) {
                p.matchedRange = c;
              }
              if (quantity > c['min']) {
                p.maxRange = c;
              }
              return p;
            },
            {
              matchedRange: undefined,
              maxRange: undefined,
            }
          );
          if (matchedRange) {
            unitPrice = matchedRange['discountAmt'] || 0;
          } else {
            unitPrice = maxRange['discountAmt'] || 0;
          }
        }
      } else {
        //consider original price as it is
      }
    }
    return {
      originalPrice,
      unitPrice,
      totalPrice: quantity * unitPrice,
    };
  }
  useOrderQueuePolling({
    isPolling: isOrderPlaced,
    orderId,
    warehouseId: selectedWarehouse?.value,
    onFailed: () => {
      setIsOrderPlaced(false);
      setOrderLoading(false);
    },
    onProcessed: async () => {
      setIsOrderPlaced(false);
      success('Order edited successfully');
      setEditingVariantIndex(null);
      setEditingIndex(null);
      await fetchDeliveryPlans(
        planInfo.record._id,
        selectedDriver.vehicle.reference
      );
      setOrderLoading(false);
    },
  });
  const handleDateChange = async (newStartDate: any) => {
    setStartDate(newStartDate);
  };
  const popover = (
    <Popover id="popover-basic">
      <Popover.Body className="p-2 bg-black border-r10px text-white">
        <span>Partial Order cannot be edited</span>
      </Popover.Body>
    </Popover>
  );
  const popoverTigo = (
    <Popover id="popover-basic">
      <Popover.Body className="p-2 bg-black border-r10px text-white">
        <span>Tigo Pesa order cannot be edited</span>
      </Popover.Body>
    </Popover>
  );

  const hasScheduledOrders = details?.orders?.some(
    (val: any) => val.routeVehicle?.scheduled
  );

  const isTruthyPharmacyOrder = (value: any) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' ||
        normalized === '1' ||
        normalized === 'yes' ||
        normalized === 'y'
      );
    }
    return false;
  };

  const isPharmacyOrderForOrder = (orderVal: any) => {
    const pharmacyCandidates = [
      orderVal?.isPharmacyOrder,
      orderVal?.order?.isPharmacyOrder,
      orderVal?.record?.isPharmacyOrder,
      orderVal?.postData?.isPharmacyOrder,
      orderVal?.record?.postData?.isPharmacyOrder,
      orderVal?.order?.postData?.isPharmacyOrder,
    ];

    return pharmacyCandidates.some((value) => isTruthyPharmacyOrder(value));
  };

  const getOrderTypeLabel = (orderVal: any) => {
    return isPharmacyOrderForOrder(orderVal) ? 'Pharmacy' : 'Utrade';
  };

  const getReviewOrderUnitPrice = (orderVal: any, variantVal: any) => {
    if (isPharmacyOrderForOrder(orderVal)) {
      return Number(variantVal?.amount || 0);
    }

    return Number(
      getVariantUnitAndTotalPriceByQuantityType(
        variantVal?.clonedQuantityType,
        variantVal?.stockCount
      )?.unitPrice || 0
    );
  };

  const getReviewOrderOriginalUnitPrice = (orderVal: any, variantVal: any) => {
    if (isPharmacyOrderForOrder(orderVal)) {
      return Number(variantVal?.originalAmount ?? variantVal?.amount ?? 0);
    }

    return Number(
      getVariantUnitAndTotalPriceByQuantityType(
        variantVal?.clonedQuantityType,
        variantVal?.stockCount
      )?.originalPrice || 0
    );
  };

  return (
    <>
      <div className="p-5">
        {outForDelivery ? (
          <AddOrderPlan
            show={outForDelivery}
            onHide={async (data: any) => {
              if (data.length) {
                setoutForDelivery(false);
                setFetchLoader(true);
                await fetchDeliveryPlans(
                  planInfo.record._id,
                  selectedDriver.vehicle.reference
                );
                setFetchLoader(false);
              } else {
                setoutForDelivery(false);
              }
            }}
            handleAddOrderToList={handleAddOrderToList}
            loading={loading}
            routeId={planInfo.record._id}
            vehicleId={selectedDriver.vehicle.reference}
            vehicle={selectedDriver.vehicle}
            moduleName={OrdersDelivery}
            districtIds={districtIds}
          />
        ) : (
          <></>
        )}
        {confirmModal ? (
          <ConfirmChanges
            show={confirmModal}
            title={'Are you sure you want to remove this item?'}
            save={'Yes'}
            message={''}
            onHide={() => {
              setConfirm(null);
              setConfirmModal(false);
            }}
            removeMsg={
              'This is the last item in the order, removing this item will cancel the order'
            }
            isRemove={!confirm.isRemove && details.orders.length > 1}
            onSave={() => {
              handleRemoveProduct(
                confirm.isRemove,
                confirm.vIndex,
                confirm.index,
                confirm.product
              );
            }}
            loading={cancelLoading}
          />
        ) : (
          <></>
        )}
        <Row className="align-items-center g-md-8 g-6 mb-5">
          <Col xs={12}>
            <Row className="align-items-center g-3">
              <Col xs>
                <h1 className="fs-22 mb-0 fw-bolder">Order List</h1>
              </Col>
              {!fetchLoader && planInfo.record.reviewRemain ? (
                <Col
                  xs={6}
                  className="d-flex justify-content-end align-items-center"
                >
                  {singleVehicle ? (
                    <></>
                  ) : (
                    <>
                      {' '}
                      <div className="min-w-lg-150px me-4">
                        <CustomSelect
                          backgroundColor="white"
                          value={{
                            value: selectedDriver.vehicle.reference,
                            label:
                              selectedDriver.vehicle.name.length > 25
                                ? selectedDriver.vehicle.name.substring(0, 25) +
                                  '...'
                                : selectedDriver.vehicle.name +
                                  '-' +
                                  selectedDriver.vehicle.number,
                            _id: selectedDriver.vehicle.reference,
                            image: selectedDriver.vehicle.image,
                            title: selectedDriver.vehicle.name,
                          }}
                          isDisabled={loading}
                          options={
                            vehicleInfo.length
                              ? vehicleInfo.map((catval: any) => {
                                  return {
                                    value: catval.vehicle.reference,
                                    label:
                                      catval.vehicle.name +
                                      '-' +
                                      catval.vehicle.number,
                                    _id: catval.vehicle.reference,
                                    image: catval.vehicle.image,
                                    title: catval.vehicle.name,
                                    ...catval,
                                  };
                                })
                              : []
                          }
                          onChange={(event: any) => {
                            handleChange(event);
                          }}
                          isMulti={false}
                        />
                      </div>
                    </>
                  )}

                  {Method.hasPermission(OrdersDelivery, Edit, currentUser) ? (
                    <>
                      {Method.checkSameDate(
                        Method.getTodayDate('YYYY-MM-DD'),
                        Method.convertDateToFormat(
                          planInfo.record.date,
                          'YYYY-MM-DD'
                        )
                      ) ? (
                        <></>
                      ) : (
                        <>
                          {!showSelectDateModal ? (
                            <>
                              <Button
                                className="uw-btn uw-btn-primary"
                                onClick={() => {
                                  setSelectDateModal(!showSelectDateModal);
                                }}
                                disabled={
                                  orderLoading ||
                                  dateLoading ||
                                  Method.checkSameDate(
                                    Method.getTodayDate('YYYY-MM-DD'),
                                    Method.convertDateToFormat(
                                      planInfo.record.date,
                                      'YYYY-MM-DD'
                                    )
                                  )
                                }
                              >
                                {dateLoading && (
                                  <span
                                    className="indicator-progress"
                                    style={{
                                      display: 'block',
                                    }}
                                  >
                                    Please wait...
                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                  </span>
                                )}
                                {!dateLoading && (
                                  <> Change Route Plan Schedule Date</>
                                )}
                              </Button>
                            </>
                          ) : (
                            <>
                              <div>
                                {showSelectDateModal ? (
                                  <>
                                    <SelectPlanDateReview
                                      show={showSelectDateModal}
                                      onSave={(date: any) => {
                                        handleDateChange(date);
                                        changePlanDate(date);
                                      }}
                                      text={
                                        'Schedule Plan for the following date'
                                      }
                                      onHide={(date: any) => {
                                        setSelectDateModal(
                                          !showSelectDateModal
                                        );
                                      }}
                                      loading={dateLoading}
                                      warehouse={selectedWarehouse?.value}
                                    />
                                  </>
                                ) : (
                                  <></>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {(Method.hasPermission(
                        OrdersDelivery,
                        Edit,
                        currentUser
                      ) ||
                        Method.hasPermission(
                          OrdersDelivery,
                          Delete,
                          currentUser
                        )) &&
                        (hasScheduledOrders ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip className="tooltip w-lg-275px">
                                <p className="text-white mb-0 fs-14 fw-500">
                                  Scheduled Orders are not removable.
                                </p>
                              </Tooltip>
                            }
                          >
                            <span className="d-inline-block">
                              <Button
                                className="uw-btn uw-btn-danger"
                                style={{
                                  pointerEvents: 'none',
                                }}
                                disabled
                              >
                                Remove orders
                              </Button>
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            className="uw-btn uw-btn-danger"
                            onClick={() => {
                              setRemoveOrderEnable(!removeOrderEnable);
                              setSelectedOrders([]);
                            }}
                          >
                            {removeOrderEnable ? 'Cancel' : 'Remove orders'}
                          </Button>
                        ))}
                      {selectedOrders.length > 0 && (
                        <Button
                          className="uw-btn uw-btn-danger"
                          onClick={() => {
                            handleRemoveOrderFromList(selectedOrders);
                          }}
                          disabled={orderRemoveLoader}
                        >
                          {orderRemoveLoader && (
                            <span
                              className="indicator-progress"
                              style={{
                                display: 'block',
                              }}
                            >
                              Please wait...
                              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                          )}
                          {!orderRemoveLoader && <>Remove</>}
                        </Button>
                      )}
                      {!orderRemoveLoader && (
                        <Button
                          className="uw-btn uw-btn-primary"
                          onClick={() => {
                            const orderIds = details.orders
                              .filter(
                                (order: any) => !order.routeVehicle?.scheduled
                              )
                              .map((order: any) => order._id); // Collect all order _ids
                            setSelectedOrders(orderIds); // Set state once after the loop
                            setRemoveOrderEnable(true);
                          }}
                          disabled={orderRemoveLoader}
                        >
                          {!orderRemoveLoader && <>Select All</>}
                        </Button>
                      )}
                      <Button
                        className="uw-btn uw-btn-primary"
                        onClick={() => {
                          completeReview();
                        }}
                        disabled={details.orders.length == 0 || orderLoading}
                      >
                        {loading && (
                          <span
                            className="indicator-progress"
                            style={{
                              display: 'block',
                            }}
                          >
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </span>
                        )}
                        {!loading && <> Complete Review</>}
                      </Button>
                    </>
                  ) : (
                    <></>
                  )}
                </Col>
              ) : (
                <></>
              )}
              {details && Object.keys(details).length && (
                <>
                  <Col xs="auto">
                    {Method.hasPermission(OrdersDelivery, Edit, currentUser) ? (
                      <Button
                        className="uw-btn uw-btn-primary"
                        onClick={() => {
                          setEditingVariantIndex(null);
                          setEditingIndex(null);
                          setoutForDelivery(true);
                        }}
                      >
                        Add Orders
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Col>
                </>
              )}
            </Row>
          </Col>
        </Row>
        {fetchLoader ? (
          <>
            <div className="d-flex justify-content-center">
              <Loader loading={fetchLoader} />
            </div>
          </>
        ) : (
          <>
            {details && Object.keys(details).length && (
              <Row className="g-7">
                {details?.record?.status === RouteOrderCancelled ? (
                  <></>
                ) : details?.record?.status === RouteOrderDelivered ||
                  details?.record?.status === RouteOrderOutForDelivery ? (
                  <>
                    {details?.orders?.[0]?.captureImage ||
                    details?.orders?.[0]?.routeVehicle?.capturedImage ||
                    details?.orders?.[0]?.routeVehicle?.captureImage ? (
                      <Col xs={12}>
                        <div className="alert alert-dismissible bg-light mb-0 p-7">
                          <Row className="align-items-center g-3">
                            <Col md>
                              <h4 className="fw-bolder fs-20">
                                Dispatch Photo
                              </h4>
                              <span className="fs-16 fw-500 text-muted">
                                Photo captured when vehicle was marked out for
                                delivery.
                              </span>
                            </Col>
                            <Col md="auto">
                              <ImageWithFallback
                                src={
                                  details.orders[0].captureImage ||
                                  details.orders[0].routeVehicle
                                    ?.capturedImage ||
                                  details.orders[0].routeVehicle?.captureImage
                                }
                                alt="Dispatch Capture"
                                className="img-fluid rounded border"
                                style={{
                                  maxHeight: '150px',
                                  maxWidth: '150px',
                                  objectFit: 'contain',
                                }}
                              />
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <>
                    {' '}
                    {details.orders.every((order: any) =>
                      order.routeVehicle.variants.every(
                        (variant: any) => variant.status === RouteOrderLoaded
                      )
                    ) &&
                      !hasScheduledOrders && (
                        <Col xs={12}>
                          <div className="alert alert-dismissible bg-light mb-0 p-7">
                            <Row className="align-items-center g-3">
                              <Col md>
                                <h4 className="fw-bolder fs-20">
                                  All goods are loaded in this vehicle.
                                </h4>
                                <span className="fs-16 fw-500">
                                  Please make sure all goods are loaded in this
                                  vehicle and then you mark them as out for
                                  delivery.
                                </span>
                              </Col>
                              <Col md="auto">
                                {Method.hasPermission(
                                  OrdersDelivery,
                                  Edit,
                                  currentUser
                                ) ? (
                                  <Button
                                    variant="primary"
                                    className="uw-btn uw-btn-primary uw-btn-lg btn-lg"
                                    onClick={() => setoutForDelivery(true)}
                                    disabled={details.orders.length == 0}
                                  >
                                    Mark as out for delivery
                                  </Button>
                                ) : (
                                  <></>
                                )}
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      )}
                  </>
                )}
                <Col xs={12} className="mb-8">
                  <Row className="g-6">
                    <SummaryInfoCard
                      colKey="vehicle"
                      bgClassName="bg-d4e1fc"
                      value={details?.record?.vehicle?.number}
                      label="Vehicle number"
                      colProps={{ xl: 3, md: 6, xs: 12 }}
                    />
                    {details?.record?.driver ? (
                      <SummaryInfoCard
                        colKey="driver"
                        bgClassName="bg-ccebfd"
                        value={details?.record?.driver?.name}
                        label="Driver name"
                        colProps={{ xl: 3, md: 6, xs: 12 }}
                      />
                    ) : (
                      <SummaryInfoCard
                        colKey="driver-na"
                        bgClassName="bg-light-danger"
                        value="Driver not allocated."
                        label="Driver name"
                        danger={true}
                        colProps={{ xl: 3, md: 6, xs: 12 }}
                      />
                    )}
                    <SummaryInfoCard
                      colKey="orders"
                      bgClassName="bg-ccf3f0"
                      value={`${details?.orders?.length} orders`}
                      label="Total orders"
                      colProps={{ xl: 3, md: 6, xs: 12 }}
                    />
                    <SummaryInfoCard
                      colKey="cash"
                      bgClassName={clsx(
                        details?.record?.totalCashCollection
                          ? 'bg-fbebbc'
                          : 'bg-efefef'
                      )}
                      value={`TSh ${
                        details?.record?.totalCashCollection
                          ? Method.getGeneralizedAmount(
                              details?.record?.totalCashCollection
                            )
                          : Method.getGeneralizedAmount(
                              details?.record?.pendingCashCollection
                            )
                      }`}
                      label={
                        details?.record?.totalCashCollection
                          ? 'Total cash collection'
                          : 'Total cash collected to be later'
                      }
                      colProps={{ xl: 3, md: 6, xs: 12 }}
                    />
                  </Row>
                </Col>
                {details?.orders?.length ? (
                  <>
                    {details?.orders?.map((val: any, index: number) => {
                      return (
                        <Col xs={12}>
                          <Card className="border border-r10px px-0">
                            <Card.Header className="bg-light align-items-center">
                              <Row className="w-100">
                                <Col lg={12} className="d-flex">
                                  <Col lg={6}>
                                    <div className="d-flex">
                                      {removeOrderEnable &&
                                        !val?.routeVehicle?.scheduled && (
                                          <div
                                            className="form-check form-check-custom form-check-solid"
                                            style={{ marginRight: '10px' }}
                                          >
                                            <input
                                              className="form-check-input h-22px w-22px"
                                              type="checkbox"
                                              name="appType"
                                              checked={selectedOrders?.includes(
                                                val?._id
                                              )}
                                              onClick={() => {
                                                handleOrderSelect(val?._id);
                                              }}
                                              disabled={
                                                val?.routeVehicle?.scheduled
                                              }
                                            />
                                          </div>
                                        )}
                                      <div>
                                        <span className="fs-22 fw-bolder">
                                          {val?.refKey} / {val?.customer?.name}
                                        </span>
                                      </div>
                                    </div>
                                  </Col>
                                </Col>
                              </Row>
                            </Card.Header>
                            <Card.Body className="py-0">
                              <div className="table-responsive">
                                <table className="table table-rounded table-row-bordered align-middle gs-3 gy-5 mb-0">
                                  <thead>
                                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                      <th className="col-w-30">
                                        Product name
                                      </th>
                                      <th className="col-w-10">Order Type</th>
                                      <th className="col-w-15">
                                        Unit Price
                                      </th>
                                      <th className="col-w-15">Quantity</th>
                                      <th className="col-w-15">
                                        Total Amount
                                      </th>
                                      <th className="col-w-15">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <>
                                      {val?.variants
                                        ?.filter((variantVal: any) =>
                                          val?.routeVehicle?.variants?.some(
                                            (routeVariant: any) =>
                                              routeVariant?.variant?._id ===
                                              variantVal?.variant
                                          )
                                        )
                                        ?.map(
                                          (variantVal: any, vIndex: number) => {
                                            const validationKey = `${variantVal?.quantityType}-${variantVal?.variant}`;

                                            return (
                                              <>
                                                {variantVal?.stockCount > 0 ? (
                                                  <>
                                                    <tr>
                                                      <td>
                                                        <div className="d-flex align-items-center">
                                                          <div className="symbol symbol-65px border me-4">
                                                            <ImageWithFallback
                                                              src={
                                                                val?.routeVehicle?.variants?.find(
                                                                  (val: any) =>
                                                                    val?.variant
                                                                      ?._id ==
                                                                    variantVal?.variant
                                                                )?.variant
                                                                  ?.media[0]
                                                                  ?.url
                                                              }
                                                              className="object-fit-contain"
                                                              alt=""
                                                            />
                                                          </div>
                                                          <div className="d-flex flex-column ms-5">
                                                            <span className="text-dark fw-600 fs-15 d-block">
                                                              {val?.routeVehicle?.variants
                                                                .find(
                                                                  (val: any) =>
                                                                    val?.variant
                                                                      ?._id ==
                                                                    variantVal?.variant
                                                                )
                                                                ?.variant?.title?.replace(
                                                                  /\s*\)\s*/g,
                                                                  ')'
                                                                )}
                                                            </span>
                                                            <span className="text-dark fw-500 fs-15 d-block">
                                                              {
                                                                val?.routeVehicle?.variants?.find(
                                                                  (val: any) =>
                                                                    val?.variant
                                                                      ?._id ==
                                                                    variantVal?.variant
                                                                )?.business
                                                                  ?.name
                                                              }
                                                            </span>
                                                          </div>
                                                        </div>
                                                      </td>
                                                      <td>
                                                        <span className="text-dark fw-500 fs-15 d-block">
                                                          {getOrderTypeLabel(
                                                            val
                                                          )}
                                                        </span>
                                                      </td>
                                                      <td>
                                                        <span className="fs-15 fw-500">
                                                          {isPharmacyOrderForOrder(
                                                            val
                                                          ) ? (
                                                            <>
                                                              {'TSh ' +
                                                                Method.getGeneralizedAmount(
                                                                  getReviewOrderUnitPrice(
                                                                    val,
                                                                    variantVal
                                                                  )
                                                                )}
                                                            </>
                                                          ) : variantVal
                                                              ?.clonedQuantityType
                                                              ?.discountAmtEnabled ? (
                                                            <>
                                                              {'TSh ' +
                                                                Method.getGeneralizedAmount(
                                                                  getReviewOrderUnitPrice(
                                                                    val,
                                                                    variantVal
                                                                  )
                                                                )}{' '}
                                                              <span className="text-decoration-line-through text-gray ms-1">
                                                                TSh{' '}
                                                                {Method.getGeneralizedAmount(
                                                                  getReviewOrderOriginalUnitPrice(
                                                                    val,
                                                                    variantVal
                                                                  )
                                                                )}
                                                              </span>
                                                            </>
                                                          ) : variantVal
                                                              ?.clonedQuantityType
                                                              ?.discountByQuantitiesEnabled ? (
                                                            <>
                                                              {getReviewOrderUnitPrice(
                                                                val,
                                                                variantVal
                                                              ) !==
                                                              getReviewOrderOriginalUnitPrice(
                                                                val,
                                                                variantVal
                                                              ) ? (
                                                                <>
                                                                  {'TSh ' +
                                                                    Method.getGeneralizedAmount(
                                                                      getReviewOrderUnitPrice(
                                                                        val,
                                                                        variantVal
                                                                      )
                                                                    )}{' '}
                                                                  <span className="text-decoration-line-through text-gray ms-1">
                                                                    TSh{' '}
                                                                    {Method.getGeneralizedAmount(
                                                                      getReviewOrderOriginalUnitPrice(
                                                                        val,
                                                                        variantVal
                                                                      )
                                                                    )}
                                                                  </span>
                                                                </>
                                                              ) : (
                                                                'TSh ' +
                                                                Method.getGeneralizedAmount(
                                                                  getReviewOrderUnitPrice(
                                                                    val,
                                                                    variantVal
                                                                  )
                                                                )
                                                              )}
                                                            </>
                                                          ) : (
                                                            'TSh ' +
                                                            Method.getGeneralizedAmount(
                                                              getReviewOrderUnitPrice(
                                                                val,
                                                                variantVal
                                                              )
                                                            )
                                                          )}
                                                        </span>
                                                      </td>
                                                      <td>
                                                        <span className="fs-18 fw-600">
                                                          {
                                                            // variantVal.stockCount
                                                            val?.routeVehicle?.variants?.find(
                                                              (val: any) =>
                                                                val?.variant
                                                                  ?._id ==
                                                                variantVal?.variant
                                                            )?.stockCount
                                                          }{' '}
                                                          {variantVal?.quantityType ===
                                                            CartonWithDozens ||
                                                          variantVal.quantityType ===
                                                            CartonWithPieces
                                                            ? variantVal.stockCount >
                                                              1
                                                              ? 'cartons'
                                                              : 'cartoon'
                                                            : ''}
                                                          {variantVal.quantityType ===
                                                          Dozen
                                                            ? variantVal.stockCount >
                                                              1
                                                              ? 'dozens'
                                                              : 'dozen'
                                                            : ''}
                                                          {variantVal.quantityType ===
                                                          Piece
                                                            ? variantVal.stockCount >
                                                              1
                                                              ? 'pieces'
                                                              : 'piece'
                                                            : ''}
                                                        </span>
                                                      </td>
                                                      <td className="text-left">
                                                        {ProductPriceWithoutTax(
                                                          {
                                                            productVal:
                                                              variantVal,
                                                            orderVal: val,
                                                          },
                                                          val.routeVehicle
                                                            .partialDelivery
                                                        )}
                                                      </td>
                                                      <td>
                                                        {!val.routeVehicle
                                                          .scheduled ? (
                                                          <>
                                                            {editingVariantIndex ==
                                                              vIndex &&
                                                            editingIndex ==
                                                              index ? (
                                                              <>
                                                                <div className="stepperInput d-flex align-items-center">
                                                                  <Button
                                                                    size="sm"
                                                                    className="uw-btn uw-btn-primary uw-btn-sm button button--addOnLeft"
                                                                    onClick={(
                                                                      event
                                                                    ) => {
                                                                      const currentStockCount =
                                                                        parseInt(
                                                                          variantVal.stockCount ||
                                                                            0
                                                                        );
                                                                      if (
                                                                        currentStockCount >
                                                                          0 &&
                                                                        currentStockCount -
                                                                          1 !==
                                                                          0
                                                                      ) {
                                                                        handleQuantityChange(
                                                                          currentStockCount -
                                                                            1,
                                                                          index,
                                                                          variantVal,
                                                                          vIndex,
                                                                          variantVal.quantityType,
                                                                          val.routeVehicle.variants.find(
                                                                            (
                                                                              val: any
                                                                            ) =>
                                                                              val
                                                                                .variant
                                                                                ._id ==
                                                                              variantVal.variant
                                                                          )
                                                                        );
                                                                      }
                                                                    }}
                                                                    disabled={
                                                                      orderLoading
                                                                    }
                                                                  >
                                                                    -
                                                                  </Button>
                                                                  <input
                                                                    type="text"
                                                                    value={
                                                                      variantVal.stockCount ||
                                                                      1
                                                                    }
                                                                    className="input stepperInput__input form-control "
                                                                    onChange={(
                                                                      event: any
                                                                    ) => {
                                                                      const currentStockCount =
                                                                        parseInt(
                                                                          event
                                                                            .target
                                                                            .value
                                                                        );
                                                                      handleQuantityChange(
                                                                        currentStockCount,
                                                                        index,
                                                                        variantVal,
                                                                        vIndex,
                                                                        variantVal.quantityType,
                                                                        val.routeVehicle.variants.find(
                                                                          (
                                                                            val: any
                                                                          ) =>
                                                                            val
                                                                              .variant
                                                                              ._id ==
                                                                            variantVal.variant
                                                                        )
                                                                      );
                                                                    }}
                                                                    onKeyPress={(
                                                                      event: any
                                                                    ) => {
                                                                      handleOnKeyPress(
                                                                        event
                                                                      );
                                                                    }}
                                                                    disabled={
                                                                      orderLoading
                                                                    }
                                                                  />
                                                                  <Button
                                                                    size="sm"
                                                                    className="uw-btn uw-btn-primary uw-btn-sm button button--addOnRight"
                                                                    onClick={() => {
                                                                      const currentStockCount =
                                                                        parseInt(
                                                                          variantVal.stockCount ||
                                                                            0
                                                                        );
                                                                      if (
                                                                        currentStockCount >=
                                                                        0
                                                                        //&& currentStockCount + 1 <=
                                                                        //   details.orders[index].variants[0].stockCount
                                                                      ) {
                                                                        handleQuantityChange(
                                                                          currentStockCount +
                                                                            1,
                                                                          index,
                                                                          variantVal,
                                                                          vIndex,
                                                                          variantVal.quantityType,
                                                                          val.routeVehicle.variants.find(
                                                                            (
                                                                              val: any
                                                                            ) =>
                                                                              val
                                                                                .variant
                                                                                ._id ==
                                                                              variantVal.variant
                                                                          )
                                                                        );
                                                                      }
                                                                    }}
                                                                    disabled={
                                                                      orderLoading
                                                                    }
                                                                  >
                                                                    +
                                                                  </Button>
                                                                  <Button
                                                                    variant="link"
                                                                    className="btn-flush btn-sm mx-2"
                                                                    onClick={() => {
                                                                      handleSave(
                                                                        variantVal.stockCount,
                                                                        index,
                                                                        variantVal,
                                                                        vIndex
                                                                      );
                                                                    }}
                                                                    disabled={
                                                                      orderLoading
                                                                    }
                                                                  >
                                                                    <img
                                                                      src={
                                                                        greenCheck
                                                                      }
                                                                      alt=""
                                                                      height={
                                                                        35
                                                                      }
                                                                      width={35}
                                                                    />
                                                                  </Button>
                                                                  <Button
                                                                    variant=""
                                                                    className="btn-flush btn-sm mx-2"
                                                                    onClick={() =>
                                                                      handleSaveClick(
                                                                        index
                                                                      )
                                                                    }
                                                                    disabled={
                                                                      orderLoading
                                                                    }
                                                                  >
                                                                    <img
                                                                      src={
                                                                        CrossIcon
                                                                      }
                                                                      alt=""
                                                                    />
                                                                  </Button>
                                                                  <Button
                                                                    variant="link"
                                                                    className="btn-flush"
                                                                    disabled={
                                                                      orderLoading
                                                                    }
                                                                    onClick={() => {
                                                                      setConfirm(
                                                                        {
                                                                          isRemove:
                                                                            val.variants.filter(
                                                                              (
                                                                                val: any
                                                                              ) =>
                                                                                val.stockCount >
                                                                                0
                                                                            )
                                                                              .length >
                                                                            1,
                                                                          vIndex:
                                                                            vIndex,
                                                                          index:
                                                                            index,
                                                                          product:
                                                                            val.variants.filter(
                                                                              (
                                                                                val: any
                                                                              ) =>
                                                                                val.stockCount >
                                                                                0
                                                                            )
                                                                              .length ==
                                                                            1
                                                                              ? val._id
                                                                              : variantVal,
                                                                        }
                                                                      );
                                                                      setConfirmModal(
                                                                        true
                                                                      );
                                                                    }}
                                                                  >
                                                                    <img
                                                                      src={
                                                                        DeleteIcon
                                                                      }
                                                                      alt=""
                                                                    />
                                                                  </Button>
                                                                </div>
                                                                {validation[
                                                                  validationKey
                                                                ]?.isInvalid ? (
                                                                  <label
                                                                    htmlFor=""
                                                                    className="fs-14 fw-500 mb-3 text-danger"
                                                                  >
                                                                    You can add
                                                                    up to{' '}
                                                                    {
                                                                      validation[
                                                                        validationKey
                                                                      ]
                                                                        .remainingQty
                                                                    }{' '}
                                                                    products
                                                                  </label>
                                                                ) : (
                                                                  <></>
                                                                )}
                                                              </>
                                                            ) : (
                                                              <>
                                                                {val
                                                                  .routeVehicle
                                                                  .partialDelivery ||
                                                                val.payment
                                                                  .paymentMode ===
                                                                  RouteOrderTigoPesa ? (
                                                                  <>
                                                                    {Method.hasPermission(
                                                                      OrdersDelivery,
                                                                      Edit,
                                                                      currentUser
                                                                    ) ? (
                                                                      <OverlayTrigger
                                                                        trigger="hover"
                                                                        placement="bottom"
                                                                        overlay={
                                                                          val
                                                                            .payment
                                                                            .paymentMode ===
                                                                          RouteOrderTigoPesa
                                                                            ? popoverTigo
                                                                            : popover
                                                                        }
                                                                      >
                                                                        <Button
                                                                          variant="link"
                                                                          className="btn-flush me-2"
                                                                        >
                                                                          <img
                                                                            src={
                                                                              editIcon
                                                                            }
                                                                            alt="Edit"
                                                                          />
                                                                        </Button>
                                                                      </OverlayTrigger>
                                                                    ) : (
                                                                      <></>
                                                                    )}
                                                                    {Method.hasPermission(
                                                                      OrdersDelivery,
                                                                      Delete,
                                                                      currentUser
                                                                    ) ? (
                                                                      <OverlayTrigger
                                                                        trigger="hover"
                                                                        placement="bottom"
                                                                        overlay={
                                                                          val
                                                                            .payment
                                                                            .paymentMode ===
                                                                          RouteOrderTigoPesa
                                                                            ? popoverTigo
                                                                            : popover
                                                                        }
                                                                      >
                                                                        <Button
                                                                          variant="link"
                                                                          className="btn-flush me-2"
                                                                        >
                                                                          <img
                                                                            src={
                                                                              DeleteIcon
                                                                            }
                                                                            alt=""
                                                                          />
                                                                        </Button>
                                                                      </OverlayTrigger>
                                                                    ) : (
                                                                      <></>
                                                                    )}
                                                                  </>
                                                                ) : (
                                                                  <>
                                                                    {Method.hasPermission(
                                                                      OrdersDelivery,
                                                                      Edit,
                                                                      currentUser
                                                                    ) ? (
                                                                      <Button
                                                                        variant="link"
                                                                        className="btn-flush me-2"
                                                                        onClick={() =>
                                                                          handleEditClick(
                                                                            index,
                                                                            vIndex
                                                                          )
                                                                        }
                                                                        disabled={
                                                                          orderLoading
                                                                        }
                                                                      >
                                                                        <img
                                                                          src={
                                                                            editIcon
                                                                          }
                                                                          alt="Edit"
                                                                        />
                                                                      </Button>
                                                                    ) : (
                                                                      <></>
                                                                    )}
                                                                    {Method.hasPermission(
                                                                      OrdersDelivery,
                                                                      Edit,
                                                                      currentUser
                                                                    ) ? (
                                                                      <Button
                                                                        variant="link"
                                                                        className="btn-flush"
                                                                        onClick={() => {
                                                                          setConfirm(
                                                                            {
                                                                              isRemove:
                                                                                val.variants.filter(
                                                                                  (
                                                                                    val: any
                                                                                  ) =>
                                                                                    val.stockCount >
                                                                                    0
                                                                                )
                                                                                  .length >
                                                                                1,
                                                                              vIndex:
                                                                                vIndex,
                                                                              index:
                                                                                index,
                                                                              product:
                                                                                val.variants.filter(
                                                                                  (
                                                                                    val: any
                                                                                  ) =>
                                                                                    val.stockCount >
                                                                                    0
                                                                                )
                                                                                  .length ==
                                                                                1
                                                                                  ? val._id
                                                                                  : variantVal,
                                                                            }
                                                                          );
                                                                          setConfirmModal(
                                                                            true
                                                                          );
                                                                        }}
                                                                        disabled={
                                                                          orderLoading
                                                                        }
                                                                      >
                                                                        <img
                                                                          src={
                                                                            DeleteIcon
                                                                          }
                                                                          alt=""
                                                                        />
                                                                      </Button>
                                                                    ) : (
                                                                      <></>
                                                                    )}
                                                                  </>
                                                                )}{' '}
                                                              </>
                                                            )}
                                                          </>
                                                        ) : (
                                                          <>
                                                            {' '}
                                                            <span className="fs-18 fw-600 text-warning">
                                                              Scheduled Orders
                                                              are not editable
                                                            </span>
                                                          </>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  </>
                                                ) : (
                                                  <></>
                                                )}
                                              </>
                                            );
                                          }
                                        )}
                                    </>
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </>
                ) : (
                  <></>
                )}
              </Row>
            )}
          </>
        )}
      </div>
    </>
  );
};
export default ReviewOrderList;
